import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Star } from "lucide-react";
import api, { money } from "../lib/api";
import ProductCard from "../components/ProductCard";
import TrustBar from "../components/TrustBar";
import SEO from "../components/SEO";

const HERO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_fragrance-luxury-29/artifacts/2fqee0j4_Shaher%204.png";
const CRAFT = "https://customer-assets-lxgj4vgw.emergentagent.net/job_fragrance-luxury-29/artifacts/018ggpyl_87F78894-1C48-4328-A480-EE05027215FE.PNG";
const HOME_AF = "https://customer-assets-lxgj4vgw.emergentagent.net/job_fragrance-luxury-29/artifacts/zch63v7i_F8DAA1E8-889F-43F9-8215-233D921A96C7.PNG";

const MARQUEE = ["PERFUMES", "AIR FRESHENERS", "SKINCARE", "SOAPS", "GIFTING"];

export default function Home() {
  const [best, setBest] = useState([]);
  const [newArr, setNewArr] = useState([]);
  const [spotlight, setSpotlight] = useState(null);

  useEffect(() => {
    api.get("/products", { params: { best_seller: true } }).then((r) => {
      setBest(r.data.slice(0, 4));
      const s = r.data.find((p) => p.name.includes("Badee Al Irtiqa Gold")) || r.data[0];
      setSpotlight(s);
    });
    api.get("/products", { params: { new_arrival: true } }).then((r) => setNewArr(r.data.slice(0, 4)));
  }, []);

  return (
    <div className="bg-[#0B0B0C]">
      <SEO jsonLd={{ "@context": "https://schema.org", "@type": "Store", name: "Queen MJ Maison Beaute", url: "https://www.queenmjmaison.store", telephone: "+1-904-219-6276", address: { "@type": "PostalAddress", streetAddress: "Encore Court", addressLocality: "Jacksonville", addressRegion: "FL", postalCode: "32205", addressCountry: "US" }, description: "A premium fragrance destination for elegant perfumes and beautiful home fragrances.", image: "https://customer-assets-lxgj4vgw.emergentagent.net/job_fragrance-luxury-29/artifacts/2fqee0j4_Shaher%204.png" }} />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#0B0B0C]" data-testid="hero-section">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(60% 70% at 75% 50%, rgba(212,175,55,0.22) 0%, rgba(11,11,12,0) 70%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center pt-28 pb-14 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="max-w-2xl order-2 lg:order-1">
            <p className="eyebrow mb-5">LUXURY FRAGRANCE · UNITED STATES</p>
            <h1 className="font-serif-lux text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] text-[#FAFAF7]">
              Wear Your <span className="gold-text italic">Signature</span>, Not a Trend
            </h1>
            <p className="text-[#FAFAF7]/70 mt-6 text-base sm:text-lg font-light max-w-lg leading-relaxed">
              Rare perfumes and artisanal home products, composed by master perfumers. Discover the scent that becomes unmistakably you.
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to="/shop" data-testid="hero-shop-button" className="btn-gold px-8 py-3.5 rounded-full text-sm inline-flex items-center gap-2">
                Explore the Collection <ArrowRight size={16} />
              </Link>
              <Link to="/shop?category=Home%20Fragrance" className="btn-outline-gold px-8 py-3.5 rounded-full text-sm">
                Home Fragrance
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }} className="order-1 lg:order-2 relative mx-auto w-full max-w-[300px] sm:max-w-[360px] lg:max-w-[440px]">
            <div className="absolute -inset-3 rounded-[2rem] gold-border opacity-60" />
            <img src={HERO} alt="Shaher Eau de Parfum — Queen MJ Maison Beaute" className="relative w-full aspect-[2/3] object-cover rounded-[1.5rem] shadow-[0_40px_80px_rgba(0,0,0,0.6)]" />
            <Link to="/product/shaher-al-wataniah" data-testid="hero-product-link" className="absolute bottom-4 left-4 right-4 flex items-center justify-between bg-[#0B0B0C]/80 backdrop-blur-md gold-border rounded-full px-5 py-3 text-sm text-[#FAFAF7] hover:bg-[#0B0B0C]/95 transition-colors">
              <span><span className="eyebrow text-[0.5rem] block">FEATURED</span>Shaher · Eau de Parfum</span>
              <ArrowRight size={16} className="text-[#D4AF37]" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[rgba(212,175,55,0.18)] py-5 overflow-hidden">
        <div className="flex whitespace-nowrap marquee-track">
          {[...MARQUEE, ...MARQUEE, ...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={i} className="font-serif-lux text-2xl text-[#FAFAF7]/30 mx-8 flex items-center gap-8">
              {m} <Sparkles size={14} className="text-[#D4AF37]/50" />
            </span>
          ))}
        </div>
      </section>

      <TrustBar />

      {/* BEST SELLERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 sm:py-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="eyebrow mb-3">MOST COVETED</p>
            <h2 className="font-serif-lux text-4xl sm:text-5xl font-light text-[#FAFAF7]">Best Sellers</h2>
          </div>
          <Link to="/shop?best=1" className="hidden sm:inline-flex items-center gap-2 text-sm text-[#D4AF37] link-underline">View all <ArrowRight size={15} /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
          {best.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* EDITORIAL SPLIT */}
      <section className="relative">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative h-[400px] lg:h-[640px]">
            <img src={CRAFT} alt="Badee Al Irtiqa Noir by Queen MJ Maison Beaute" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="bg-[#141416] flex items-center px-6 sm:px-12 lg:px-20 py-16">
            <div>
              <p className="eyebrow mb-4">THE CRAFT</p>
              <h2 className="font-serif-lux text-4xl sm:text-5xl font-light text-[#FAFAF7] leading-tight mb-6">
                Composed with the <span className="gold-text italic">finest</span> ingredients
              </h2>
              <p className="text-[#FAFAF7]/60 leading-relaxed font-light mb-4">
                Every Queen MJ Maison Beaute fragrance begins with the finest raw materials — May rose, Calabrian bergamot, Laotian oud — hand-selected and aged to reveal their full character.
              </p>
              <p className="text-[#FAFAF7]/60 leading-relaxed font-light mb-8">
                Our perfumers compose in small batches, layering top, heart and base notes into a scent pyramid that unfolds over hours on the skin.
              </p>
              <Link to="/about" className="btn-outline-gold px-7 py-3 rounded-full text-sm inline-flex items-center gap-2">
                Our Story <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SIGNATURE SPOTLIGHT */}
      {spotlight && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 sm:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, scale: 0.94 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="relative">
              <div className="absolute -inset-6 rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.45), transparent 70%)" }} />
              <img src={spotlight.images?.[0]} alt={spotlight.name} className="relative w-full max-h-[560px] object-contain rounded-2xl gold-border" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <p className="eyebrow mb-4">SIGNATURE SPOTLIGHT · {spotlight.brand}</p>
              <h2 className="font-serif-lux text-4xl sm:text-5xl font-light text-[#FAFAF7] leading-tight mb-5">{spotlight.name}</h2>
              <p className="text-[#FAFAF7]/65 leading-relaxed font-light mb-6">{spotlight.description}</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {[...(spotlight.notes?.top || []), ...(spotlight.notes?.heart || []), ...(spotlight.notes?.base || [])].slice(0, 6).map((n) => (
                  <span key={n} className="text-xs text-[#FAFAF7]/70 bg-white/5 px-3 py-1.5 rounded-full">{n}</span>
                ))}
              </div>
              <div className="flex items-center gap-6">
                <span className="font-serif-lux text-3xl text-[#D4AF37]">{money(spotlight.price)}</span>
                <Link to={`/product/${spotlight.id}`} data-testid="spotlight-shop-button" className="btn-gold px-8 py-3.5 rounded-full text-sm inline-flex items-center gap-2">
                  Discover <ArrowRight size={16} />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 sm:py-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="eyebrow mb-3">JUST LANDED</p>
            <h2 className="font-serif-lux text-4xl sm:text-5xl font-light text-[#FAFAF7]">New Arrivals</h2>
          </div>
          <Link to="/shop?new=1" className="hidden sm:inline-flex items-center gap-2 text-sm text-[#D4AF37] link-underline">View all <ArrowRight size={15} /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
          {newArr.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* HOME FRAGRANCE BANNER */}
      <section className="relative h-[480px] flex items-center overflow-hidden">
        <img src={HOME_AF} alt="Sabah Al Ward air freshener" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full text-center">
          <p className="eyebrow mb-4">SCENT YOUR SANCTUARY · 3 FOR $25</p>
          <h2 className="font-serif-lux text-4xl sm:text-6xl font-light text-[#FAFAF7] max-w-3xl mx-auto leading-tight">
            Luxury air fresheners for the home — any 3 for $25
          </h2>
          <Link to="/shop?subcategory=Air%20Fresheners" className="btn-gold px-8 py-3.5 rounded-full text-sm inline-flex items-center gap-2 mt-8">
            Shop Air Fresheners <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* PRESS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20">
        <p className="eyebrow text-center mb-10">AS SEEN IN</p>
        <div className="flex flex-wrap justify-center items-center gap-x-14 gap-y-6">
          {["VOGUE", "Harper's BAZAAR", "ELLE", "GQ", "Vanity Fair"].map((m) => (
            <span key={m} className="font-serif-lux text-2xl sm:text-3xl text-[#FAFAF7]/35 italic">{m}</span>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            ["Badee Al Irtiqa Noir is the only scent I get complimented on. Utterly addictive.", "Amara L."],
            ["My home has never smelled so luxurious. The Sabah Al Ward air freshener is divine.", "Jordan R."],
            ["Packaging, scent, longevity — everything about Queen MJ Maison Beaute feels couture.", "Priya M."],
          ].map(([q, n], i) => (
            <div key={i} className="bg-[#141416] gold-border rounded-xl p-8">
              <div className="flex gap-1 mb-4">{Array(5).fill(0).map((_, j) => <Star key={j} size={14} className="fill-[#D4AF37] text-[#D4AF37]" />)}</div>
              <p className="font-serif-lux text-lg text-[#FAFAF7]/85 italic leading-relaxed mb-4">"{q}"</p>
              <p className="eyebrow text-[0.6rem]">{n} · Verified Buyer</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
